/**
 * Store Reset Utility
 *
 * Provides centralized store reset functionality to prevent data leakage
 * between user sessions. This utility:
 * - Maintains a registry of store reset functions
 * - Detects session conflicts (different user on same browser)
 * - Provides resetAllStores() to clear all application state
 */

// Registry of store reset functions
const storeResetRegistry: Map<string, () => void> = new Map();

// Session storage key for tracking last active user
const SESSION_USER_KEY = 'nucleus_active_user_id';

/**
 * Register a store's reset function
 * @param storeName - Unique identifier for the store
 * @param resetFn - Function that resets the store to initial state
 */
export const registerStoreReset = (storeName: string, resetFn: () => void): void => {
  storeResetRegistry.set(storeName, resetFn);
  console.log(`Store registered for reset: ${storeName}`);
};

/**
 * Unregister a store's reset function
 * @param storeName - Unique identifier for the store
 */
export const unregisterStoreReset = (storeName: string): void => {
  storeResetRegistry.delete(storeName);
};

/**
 * Reset all registered stores to their initial state
 * This should be called on logout or when session conflict is detected
 */
export const resetAllStores = (): void => {
  console.log('Resetting all stores...');

  storeResetRegistry.forEach((resetFn, storeName) => {
    try {
      resetFn();
      console.log(`Store reset successfully: ${storeName}`);
    } catch (error) {
      console.error(`Failed to reset store ${storeName}:`, error);
    }
  });

  // Clear session user tracking
  sessionStorage.removeItem(SESSION_USER_KEY);

  console.log('All stores reset complete');
};

/**
 * Get the last active user ID from session storage
 */
export const getLastActiveUserId = (): string | null => {
  return sessionStorage.getItem(SESSION_USER_KEY);
};

/**
 * Set the active user ID in session storage
 * @param userId - The user ID to track
 */
export const setActiveUserId = (userId: string): void => {
  sessionStorage.setItem(SESSION_USER_KEY, userId);
};

/**
 * Check if there's a session conflict (different user trying to login)
 * @param newUserId - The user ID attempting to login
 * @returns true if there's a conflict, false otherwise
 */
export const hasSessionConflict = (newUserId: string): boolean => {
  const lastUserId = getLastActiveUserId();

  // No conflict if no previous user or same user
  if (!lastUserId || lastUserId === newUserId) {
    return false;
  }

  console.log(`Session conflict detected: Previous user ${lastUserId}, New user ${newUserId}`);
  return true;
};

/**
 * Handle session transition - reset stores if different user
 * @param newUserId - The user ID that is logging in
 * @returns true if stores were reset due to conflict
 */
export const handleSessionTransition = (newUserId: string): boolean => {
  const hadConflict = hasSessionConflict(newUserId);

  if (hadConflict) {
    console.log('Session conflict detected, resetting all stores...');
    resetAllStores();
  }

  // Update the active user
  setActiveUserId(newUserId);

  return hadConflict;
};

/**
 * Clear all session tracking data
 * Call this on explicit logout
 */
export const clearSessionTracking = (): void => {
  sessionStorage.removeItem(SESSION_USER_KEY);
};

/**
 * Get the number of registered stores
 * Useful for debugging
 */
export const getRegisteredStoreCount = (): number => {
  return storeResetRegistry.size;
};

/**
 * Get list of registered store names
 * Useful for debugging
 */
export const getRegisteredStoreNames = (): string[] => {
  return Array.from(storeResetRegistry.keys());
};
